import React, { useMemo } from "react";
import { MarketHeaderProps } from "./types";
import { Grid } from "components/src/components/Grid/Grid";
import { Icon } from "components/src/components/Icon/Icon";
import cx from "components/src/utils/cx";
import { tailwindSemanticBgColors as twColors } from "shared/src/styles/colorStyles";
import { spanMap, getPaddingClasses } from "./js/MarketHeaderHelpers";
import { Caption } from "./components/Caption";
import { MetaData } from "./components/MetaData";
import { MarketHeaderLabelsRow } from "./components/MarketHeaderLabelsRow";
import { MarketHeaderCompact } from "./components/MarketHeaderCompact";

export const MarketHeader = ({
  caption,
  labels = [],
  details,
  title,
  description,
  bg = "neutrals-transparent",
  noBorder,
  noLabels,
  hasSGP,
  isOddsBoost,
  className,
  preserveGridCells = true,
  useSideSGPLabel = false,
  variant = "default",
  paddingClasses,
  labelTextSize,
  labelTextColor,
  descriptionTextSize,
  descriptionTextColor,
  ...rest
}: MarketHeaderProps) => {
  const numLabels = labels?.length;

  if (variant === "compact") {
    return <MarketHeaderCompact labels={labels} details={details} description={description} hasSGP={hasSGP} isOddsBoost={isOddsBoost} className={className} bg={bg} paddingClasses={paddingClasses} labelTextSize={labelTextSize} labelTextColor={labelTextColor} descriptionTextSize={descriptionTextSize} descriptionTextColor={descriptionTextColor} {...rest} />;
  }

  if (!labels?.length && !description && !title && !caption) return null;

  const _descriptionTextSize = descriptionTextSize ?? "body-xs";
  const _descriptionTextColor = descriptionTextColor ?? "cui-text-fg-moderate";

  const gridContent = useMemo(() => (
    <Grid
      className={cx(
        {
          [twColors[bg]]: bg !== "none",
          "cui-w-full": useSideSGPLabel,
          [`${getPaddingClasses(paddingClasses)} cui-border-0 cui-border-solid cui-border-b-1 cui-border-fg-minimal`]: !noBorder,
        },
        `${className}`
      )}
      {...rest}
      flush
    >
      <MetaData
        hasCaption={!!caption}
        hasDetails={Array.isArray(details) && !!details.length}
        details={details}
        hasSGP={hasSGP}
        numLabels={numLabels}
        title={title}
        isOddsBoost={isOddsBoost}
        description={description}
      />
      {caption ? (
        <Caption caption={caption} numberOfLabels={numLabels} />
      ) : preserveGridCells ? <Grid.Cell /> : null}
      {!title && !description && caption && <Grid.Cell colSpan={spanMap[numLabels]} />}
      <Grid.Cell colSpan="full" className={`cui__market-header-description ${_descriptionTextSize} ${_descriptionTextColor}`}>
        {description ?? null}
      </Grid.Cell>
      <MarketHeaderLabelsRow labels={labels} numLabels={numLabels} isOddsBoost={isOddsBoost} noLabels={noLabels} textSize={labelTextSize} textColor={labelTextColor} />
    </Grid>
  ), [labels, numLabels, description, caption, title, bg, className, details, hasSGP, preserveGridCells, noBorder, isOddsBoost, rest, useSideSGPLabel, paddingClasses, labelTextSize, labelTextColor, _descriptionTextSize, _descriptionTextColor]);

  if (useSideSGPLabel) {
    return (
      <div className="cui-flex cui-gap-sm">
        {gridContent}
        <Icon name="ui_sgp" color="fg-primary-strong" size="lg" />
      </div>
    );
  }

  return gridContent;
};
